import { useEffect, useState } from 'react'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../store/hooks'
import { CheckpointRequest, useCreateCheckpointMutation, useGetCheckpointQuery, useUpdateCheckpointMutation } from '../../../../../store/sync/sync'
import { useGetMeQuery, useUpdateUserIntegrationMutation } from '../../../../../store/user/user'
import { WealthboxInboundContactSyncSettingsFilters, wealthboxSettingsDefaultValues, WealthboxSettingsFormValues } from './models'
import { useGetIntegrationEntitiesQuery, WealthboxTag } from '../../../../../store/integrations/integrations'
import { IntegrationServiceId } from '../../../../../common/integrations'
import { MultiSelectDropdown, MultiSelectOption } from '../../../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { getAccountCRMIntegrationConfig } from '../../../../../models/Account'
import { merge } from '../../../utils'
import { removeEmptyItems } from '../../../../../common/util'
import { SelectDropdown, SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import { Tooltip, TooltipProps } from '../../../../../components/Tooltip/Tooltip'
import { Col, Container, Form, OverlayTrigger, Row, Stack } from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import TagsInput from 'react-tagsinput'
import { Button } from '../../../../../components/Button/Button'

export const WealthboxContactSyncSettings = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: 10 })
  const [autoSync, setAutoSync] = useState<boolean | null>(false)
  const checkpointRequest = {
    accountId,
    level: 'user',
    connector: 'wealthbox',
    direction: 'inbound',
    object: 'contact',
  } as CheckpointRequest
  const { data: checkpoint, isFetching: isGetCheckpointFetching } = useGetCheckpointQuery(checkpointRequest)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const [createCheckpoint, { isLoading: isCreateCheckpointLoading }] = useCreateCheckpointMutation()
  const [updateCheckpoint, { isLoading: isUpdateCheckpointLoading }] = useUpdateCheckpointMutation()
  const { data: userData, isFetching: isGetMeQueryFetching } = useGetMeQuery({ accountId: accountId })
  const form = useForm<WealthboxSettingsFormValues>({ defaultValues: wealthboxSettingsDefaultValues })
  const [updateUserIntegration, { isLoading: isUpdateUserIntegrationLoading }] = useUpdateUserIntegrationMutation()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [serviceConfig, setServiceConfig] = useState<{ [key: string]: any } | null>(null)
  const [isSyncDisabled, setIsSyncDisabled] = useState<boolean>(true)

  const { data: tags, isFetching: isTagsLoading } = useGetIntegrationEntitiesQuery({
    accountId,
    connector: IntegrationServiceId.WEALTHBOX,
    entityType: 'contact-tags',
  })

  const [tierTagSelectOptions, setTierTagSelectOptions] = useState<MultiSelectOption[] | null>(null)

  useEffect(() => {
    if (!tags || !tags.data || !tags.data.tags || tags.data.tags.length === 0) {
      setTierTagSelectOptions([])
      return
    }
    const options = tags.data.tags.map((tag: WealthboxTag) => ({ id: tag.id, label: tag.name }))
    setTierTagSelectOptions(options)
  }, [tags])

  useEffect(() => {
    if (account && account.data) {
      const serviceIntegration = getAccountCRMIntegrationConfig(account.data, 'wealthbox')
      setIsSyncDisabled(serviceIntegration?.isSyncDisabled ?? false)
    }
  }, [account])

  useEffect(() => {
    setAutoSync(checkpoint?.data?.active ?? null)
  }, [checkpoint])

  useEffect(() => {
    form.setValue('inbound.contact.autoSync', autoSync || false)
  }, [autoSync, form.setValue, form])

  useEffect(() => {
    if (userData) {
      const integration = userData.integrations?.find((i) => i.serviceId === IntegrationServiceId.WEALTHBOX)
      if (integration) {
        setServiceConfig(integration.serviceConfig ?? null)
      }
    }
  }, [userData])

  useEffect(() => {
    if (serviceConfig?.inbound?.contact?.filters) {
      form.setValue('inbound.contact.filters', serviceConfig.inbound.contact.filters)
      form.setValue('inbound.contact.filters.tags', serviceConfig.inbound.contact.filters.tags ?? [])
    }
    if (serviceConfig?.inbound?.contact?.tierTags) {
      form.setValue('inbound.contact.tierTags', serviceConfig.inbound.contact.tierTags)
    }
  }, [serviceConfig, form])

  const onSubmit = async (data: WealthboxSettingsFormValues) => {
    try {
      setIsUpdating(true)
      if (autoSync == null) {
        if (data.inbound.contact.autoSync) {
          await createCheckpoint(checkpointRequest).unwrap()
        }
      } else {
        await updateCheckpoint({ ...checkpointRequest, active: data.inbound.contact.autoSync }).unwrap()
      }
      const integration = userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.WEALTHBOX)
      if (integration) {
        const filters: WealthboxInboundContactSyncSettingsFilters = {
          active: data.inbound.contact.filters.active ?? undefined,
          contactType: data.inbound.contact.filters.contactType || undefined,
          tags: (data.inbound.contact.filters.tags ?? []).length > 0 ? data.inbound.contact.filters.tags : undefined,
          type: data.inbound.contact.filters.type || undefined,
        }

        const tierTags = (data.inbound.contact.tierTags ?? []).length > 0 ? data.inbound.contact.tierTags : null

        const serviceConfig = merge(integration.serviceConfig || {}, { inbound: { contact: { filters, tierTags } } })

        await updateUserIntegration({
          accountId,
          integrationName: integration.serviceId,
          integrationId: integration.id,
          payload: { serviceConfig: removeEmptyItems(serviceConfig) },
        })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleCancel = () => {
    form.reset()
  }

  const statusSelectOptions: Array<SelectOption> = [
    { id: '', label: '<no filter>' },
    { id: 'true', label: 'Active' },
    { id: 'false', label: 'Inactive' },
  ]

  const statusToSelectOption = (value?: boolean): SelectOption => {
    if (value === true) return statusSelectOptions[1]
    if (value === false) return statusSelectOptions[2]
    return statusSelectOptions[0]
  }

  const selectOptionToStatus = (selectOption: SelectOption): boolean | null => {
    if (selectOption.id === 'true') return true
    if (selectOption.id === 'false') return false
    return null
  }

  const typeSelectOptions: Array<SelectOption> = [
    { id: '', label: '<no filter>' },
    { id: 'person', label: 'Person' },
    { id: 'household', label: 'Household' },
    { id: 'organization', label: 'Organization' },
    { id: 'trust', label: 'Trust' },
  ]

  const typeToSelectOption = (value?: string): SelectOption => {
    if (value === 'person') return typeSelectOptions[1]
    if (value === 'household') return typeSelectOptions[2]
    if (value === 'organization') return typeSelectOptions[3]
    if (value === 'trust') return typeSelectOptions[4]
    return typeSelectOptions[0]
  }

  const selectOptionToType = (selectOption: SelectOption): string | null => {
    if (selectOption.id === '') return null
    return selectOption.id
  }

  const renderContactTypesTooltip = (props: TooltipProps) => (
    <Tooltip id='contact-types-tooltip' className='contact-types-tooltip' {...props}>
      Possible default values are:
      <ul>
        <li>
          <b>Client</b>
        </li>
        <li>
          <b>Past Client</b>
        </li>
        <li>
          <b>Prospect</b>
        </li>
        <li>
          <b>Vendor</b>
        </li>
        <li>
          <b>Organization</b>
        </li>
      </ul>
    </Tooltip>
  )

  const renderTypeTooltip = (props: TooltipProps) => (
    <Tooltip id='type-tooltip' className='type-tooltip' {...props}>
      The type of contact that should be synced
    </Tooltip>
  )

  if ((!isUpdating && (isGetMeQueryFetching || isGetCheckpointFetching)) || isTagsLoading) {
    return (
      <Container>
        <div className='skeleton mb-2' style={{ width: '100%', height: '70px' }} />
        <div className='skeleton mb-3' style={{ width: '100%', height: '70px' }} />
      </Container>
    )
  }

  return (
    <Container>
      <h6>Contacts</h6>
      <div>Keep your contacts in sync between Zocks and Wealthbox.</div>
      <Row className='mb-1 mt-3'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div>Synchronize your contacts automatically</div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <Controller
              name='inbound.contact.autoSync'
              control={form.control}
              render={({ field, fieldState }) => (
                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
                  <Form.Switch checked={field.value === true} onChange={field.onChange} disabled={isSyncDisabled || isAccountFetching} />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </ScopedBlock>
              )}
            />
          </Stack>
        </Col>
      </Row>
      <hr></hr>
      <div className={'label-large'}>Filters</div>
      <Row className='mb-1 mt-3'>
        <Col className='d-flex align-items-center'>
          <div className='d-flex gap-2'>
            <div>Type</div>
            <OverlayTrigger trigger={['hover', 'focus']} placement='top' overlay={renderTypeTooltip}>
              <div className='align-self-center'>
                <i className='bi bi-info-circle-fill' />
              </div>
            </OverlayTrigger>
          </div>
        </Col>
        <Col>
          <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
            <Controller
              name={'inbound.contact.filters.type'}
              control={form.control}
              render={({ field }) => (
                <SelectDropdown
                  className='py-2 zocks-px-0.75'
                  options={typeSelectOptions}
                  initialSelectedOption={typeToSelectOption(field.value)}
                  onOptionSelected={(s) => field.onChange(selectOptionToType(s))}
                />
              )}
            />
          </ScopedBlock>
        </Col>
      </Row>
      <Row className='mb-1 mt-3'>
        <Col className='d-flex align-items-center'>
          <div>Status</div>
        </Col>
        <Col>
          <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
            <Controller
              name={'inbound.contact.filters.active'}
              control={form.control}
              render={({ field }) => (
                <SelectDropdown
                  className='py-2 zocks-px-0.75'
                  options={statusSelectOptions}
                  initialSelectedOption={statusToSelectOption(field.value)}
                  onOptionSelected={(s) => field.onChange(selectOptionToStatus(s))}
                />
              )}
            />
          </ScopedBlock>
        </Col>
      </Row>
      <Row className='mb-1 mt-3'>
        <Col className='d-flex align-items-center'>
          <div className='d-flex gap-2'>
            <div>Contact Type</div>
            <OverlayTrigger trigger={['hover', 'focus']} placement='top' overlay={renderContactTypesTooltip}>
              <div className='align-self-center'>
                <i className='bi bi-info-circle-fill' />
              </div>
            </OverlayTrigger>
          </div>
        </Col>
        <Col>
          <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
            <Controller
              name={'inbound.contact.filters.contactType'}
              control={form.control}
              render={({ field }) => <Form.Control id={field.name} type='text' {...field} />}
            />
          </ScopedBlock>
        </Col>
      </Row>
      <Row className='mb-1 mt-3'>
        <Col className='d-flex align-items-center'>
          <div>Tags</div>
        </Col>
        <Col>
          <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
            <Controller
              name={'inbound.contact.filters.tags'}
              control={form.control}
              render={({ field }) => <TagsInput value={field.value ?? []} onChange={field.onChange} />}
            />
          </ScopedBlock>
        </Col>
      </Row>
      <hr></hr>
      <div className={'label-large'}>Tiers</div>
      <Row className='mb-1 mt-3'>
        <Col className='d-flex align-items-center'>
          <div>Tags</div>
        </Col>
        <Col>
          <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
            <Controller
              name={'inbound.contact.tierTags'}
              control={form.control}
              render={({ field }) => (
                <MultiSelectDropdown
                  name={field.name}
                  options={tierTagSelectOptions ?? []}
                  initialSelectedOptions={field.value ?? []}
                  onSelectedChanged={field.onChange}
                />
              )}
            />
          </ScopedBlock>
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col>
          <div className='d-flex gap-3 justify-content-end mb-4'>
            <Button
              variant='secondary'
              onClick={handleCancel}
              disabled={isCreateCheckpointLoading || isUpdateCheckpointLoading || isUpdateUserIntegrationLoading}
            >
              Reset
            </Button>
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Button
                variant='primary'
                onClick={form.handleSubmit(onSubmit)}
                loading={isCreateCheckpointLoading || isUpdateCheckpointLoading || isUpdateUserIntegrationLoading}
              >
                Save
              </Button>
            </ScopedBlock>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
